import type { MembershipTierId, PaginationDto as Pagination } from "@albertine/types";
import * as Sentry from "@sentry/react";
import { useCallback, useState } from "react";
import { useAgents } from "../../common/hooks/useAgents";
import { useAgentTeams } from "../../common/hooks/useAgentTeams";
import type { Agent } from "../../common/types/Agent";
import {
    type RequestDeadlineStatus,
    splitStatusDeadline,
} from "../../common/types/Deadline";
import {
    CategoriesWithUncategorized,
    categoryDisplayName,
    type FilterByCategory,
} from "../../common/types/OfferingCategory";
import type { Request } from "../../common/types/Request";
import type {
    RequestListFilters,
    RequestListSort,
} from "../../common/types/RequestList";
import { RequestStatusMap } from "../../common/types/RequestStatus";
import type { SortOrder, SortSettings } from "../../common/types/Sort";
import {
    type FilterByAssignee,
    getActiveAssigneeSelections,
    getInactiveAssigneeSelections,
    teamFilterItems,
    unassignedAgent,
} from "../../common/utils/agents.util";
import { onlyUnique } from "../../common/utils/array.util";
import { type UrlState, useUrlParams } from "../../utils/customHook.util";
import { formatTotal } from "../../utils/format.util";
import { firebaseTimestampFromDate } from "../../utils/timestamp.util";
import { isMobile } from "../../utils/window.util";
import InputSearch from "../Common/Input.Search";
import { List } from "../Common/List";
import ListFilter, { type ListFilterItem } from "../Common/ListFilter";
import PaginatedList from "../Common/list/PaginatedList";
import SortItemsBy from "../Common/SortItemsBy";
import Tag from "../Common/Tag";
import RequestCard from "./RequestCard";
import "./RequestListView.css";
import { useApiClient, useGet } from "@albertine/api-client";
import {
    type DeadlineStep,
    type RequestBookingOutcome,
    type RequestOutcome,
    RequestStatus,
} from "@albertine/types";
import type { MembershipTier } from "@albertine/types/openapi/lmt";
import { Lib } from "albertine-shared-web";
import ListFilterGroup from "../Common/ListFilterGroup";

type FilterByMembership = { id: MembershipTierId; name: string };

type FilterByRequestStatus = { id: RequestDeadlineStatus; name: string };

type FilterSettings = {
    selectedAgents: string[];
    selectedTeamIds: string[];
    selectedStatuses: RequestDeadlineStatus[];
    selectedCategories: CategoriesWithUncategorized[];
    selectedMembershipTiers: MembershipTierId[];
};
const defaultSortBy = "deadline";
const defaultSortOrder: SortOrder = "asc";

type RequestViewProps = {
    currentAgent: Agent;
};

function sortSettingsToQuery(sortSettings: SortSettings): RequestListSort | undefined {
    const { order } = sortSettings;
    if (sortSettings.by === "deadline") {
        return {
            by: "nextDeadlineAt",
            order,
        };
    }
    if (sortSettings.by === "eventDate") {
        return {
            by: "startDateTime",
            order,
        };
    }
    if (sortSettings.by === "ticketNumber") {
        const flipOrder = order === "asc" ? "desc" : "asc";
        return {
            by: "ticketNumber",
            order: flipOrder,
        };
    }
    return undefined;
}

function filtersToSearchQuery(
    searchTerm: string,
    sortSettings: SortSettings,
    filterSettings: FilterSettings,
): RequestListFilters {
    const statusDeadlineSplitted = filterSettings.selectedStatuses.map((it) =>
        splitStatusDeadline(it),
    );
    const status = statusDeadlineSplitted
        .map((it) => it.status)
        .filter((it): it is RequestStatus => it !== undefined)
        .filter(onlyUnique);
    const nextDeadlineStep = statusDeadlineSplitted
        .map((it) => it.deadlineStep)
        .filter((it): it is DeadlineStep => it !== undefined);
    const outcome = statusDeadlineSplitted
        .map((it) => it.outcome)
        .filter((it): it is RequestOutcome => it !== undefined);
    const bookingOutcome = statusDeadlineSplitted
        .map((it) => it.bookingOutcome)
        .filter((it): it is RequestBookingOutcome => it !== undefined);

    const offeringCategory = filterSettings.selectedCategories;
    const memberMembershipTier = filterSettings.selectedMembershipTiers;

    const requestListFilters: RequestListFilters = {
        searchTerm,
        status,
        offeringCategory,
        nextDeadlineStep,
        outcome,
        bookingOutcome,
        assignedTo: filterSettings.selectedAgents,
        teamIds: filterSettings.selectedTeamIds,
        memberMembershipTier,
        sort: sortSettingsToQuery(sortSettings),
    };
    return requestListFilters;
}

const RequestListView = function RequestView(props: RequestViewProps) {
    const { currentAgent } = props;
    const agents = useAgents();
    const teams = useAgentTeams();

    const [total, setTotal] = useState<number>(0);

    const { urlState, setUrlState } = useUrlParams();
    const updateUrlState = (update: Partial<UrlState>) => {
        setUrlState((previousUrlState) => ({
            ...previousUrlState,
            ...update,
        }));
    };

    const {
        sortBy,
        sortOrder,
        statusList,
        assignedTo,
        requestCategory,
        membershipTier,
        teamIds,
    } = urlState;
    const [searchTerm, setSearchTerm] = useState<string>("");
    const { data: membershipTiers = [] } = useGet("membershipTiers", {});
    const sortSettings: SortSettings = {
        by: sortBy ?? defaultSortBy,
        order: sortOrder ?? defaultSortOrder,
    };
    const filterSettings: FilterSettings = {
        selectedAgents: assignedTo ?? [],
        selectedTeamIds: teamIds ?? [],
        selectedStatuses: statusList ?? [],
        selectedCategories: requestCategory ?? [],
        selectedMembershipTiers: membershipTier ?? [],
    };

    const categoryFilterOptions: ListFilterItem<FilterByCategory>[] =
        CategoriesWithUncategorized.options.map((categoryId) => ({
            data: {
                id: categoryId,
                name: categoryDisplayName(categoryId, "en"),
            },
            selected: filterSettings.selectedCategories.includes(categoryId),
        }));

    const requestFilterOptions: ListFilterItem<FilterByRequestStatus>[] = [
        {
            data: {
                id: RequestStatus.enum.draft,
                name: RequestStatusMap.draft,
            },
            selected: filterSettings.selectedStatuses.includes(
                RequestStatus.enum.draft,
            ),
            selectIndividually: true,
            children: undefined,
        },
        {
            data: {
                id: RequestStatus.enum.new,
                name: RequestStatusMap.new,
            },
            selected: filterSettings.selectedStatuses.includes(RequestStatus.enum.new),
            selectIndividually: true,
            children: undefined,
        },
        {
            data: {
                id: RequestStatus.enum.open,
                name: RequestStatusMap.open,
            },
            selected: filterSettings.selectedStatuses.includes(RequestStatus.enum.open),
            selectIndividually: true,
            children: [
                {
                    data: {
                        id: "open__send_status_update",
                        name: "Send status update",
                    },
                    selected: filterSettings.selectedStatuses.includes(
                        "open__send_status_update",
                    ),
                },
                {
                    data: {
                        id: "open__send_proposal",
                        name: "Send proposal",
                    },
                    selected:
                        filterSettings.selectedStatuses.includes("open__send_proposal"),
                },
                {
                    data: {
                        id: "open__send_booking_confirmation",
                        name: "Send booking confirmation",
                    },
                    selected: filterSettings.selectedStatuses.includes(
                        "open__send_booking_confirmation",
                    ),
                },
                {
                    data: {
                        id: "open__wait_for_member_response",
                        name: "Wait for member response",
                    },
                    selected: filterSettings.selectedStatuses.includes(
                        "open__wait_for_member_response",
                    ),
                },
                {
                    data: {
                        id: "open__wait_for_supplier_response",
                        name: "Wait for supplier response",
                    },
                    selected: filterSettings.selectedStatuses.includes(
                        "open__wait_for_supplier_response",
                    ),
                },
            ],
        },
        {
            data: {
                id: RequestStatus.enum.confirmed,
                name: RequestStatusMap.confirmed,
            },
            selected: filterSettings.selectedStatuses.includes(
                RequestStatus.enum.confirmed,
            ),
            selectIndividually: true,
            children: [
                {
                    data: {
                        id: "confirmed__noActionNeeded",
                        name: "No further action",
                    },
                    selected: filterSettings.selectedStatuses.includes(
                        "confirmed__noActionNeeded",
                    ),
                },
                {
                    data: {
                        id: "confirmed__revisit_request",
                        name: "Revisit request",
                    },
                    selected: filterSettings.selectedStatuses.includes(
                        "confirmed__revisit_request",
                    ),
                },
                {
                    data: {
                        id: "confirmed__send_status_update",
                        name: "Send status update",
                    },
                    selected: filterSettings.selectedStatuses.includes(
                        "confirmed__send_status_update",
                    ),
                },
                {
                    data: {
                        id: "confirmed__check_on_pending_payment",
                        name: "Check on pending payment",
                    },
                    selected: filterSettings.selectedStatuses.includes(
                        "confirmed__check_on_pending_payment",
                    ),
                },
                {
                    data: {
                        id: "confirmed__firstChoiceBooked",
                        name: "First choice booked",
                    },
                    selected: filterSettings.selectedStatuses.includes(
                        "confirmed__firstChoiceBooked",
                    ),
                },
                {
                    data: {
                        id: "confirmed__alternativeChoiceBooked",
                        name: "Alternative accepted",
                    },
                    selected: filterSettings.selectedStatuses.includes(
                        "confirmed__alternativeChoiceBooked",
                    ),
                },
            ],
        },
        {
            data: {
                id: RequestStatus.enum.closed,
                name: RequestStatusMap.closed,
            },
            selected: filterSettings.selectedStatuses.includes(
                RequestStatus.enum.closed,
            ),
            selectIndividually: true,
            children: [
                {
                    data: {
                        id: "closed__noBookingNeeded",
                        name: "Information request only",
                    },
                    selected: filterSettings.selectedStatuses.includes(
                        "closed__noBookingNeeded",
                    ),
                },
                {
                    data: {
                        id: "closed__booked",
                        name: "Booked",
                    },
                    selected:
                        filterSettings.selectedStatuses.includes("closed__booked"),
                },
                {
                    data: {
                        id: "closed__notBooked",
                        name: "Not converted to booking",
                    },
                    selected:
                        filterSettings.selectedStatuses.includes("closed__notBooked"),
                },
                {
                    data: {
                        id: "closed__firstChoiceBooked",
                        name: "First choice booked",
                    },
                    selected: filterSettings.selectedStatuses.includes(
                        "closed__firstChoiceBooked",
                    ),
                },
                {
                    data: {
                        id: "closed__alternativeChoiceBooked",
                        name: "Alternative accepted",
                    },
                    selected: filterSettings.selectedStatuses.includes(
                        "closed__alternativeChoiceBooked",
                    ),
                },
                {
                    data: {
                        id: "closed__memberCancelled",
                        name: "Member cancelled",
                    },
                    selected: filterSettings.selectedStatuses.includes(
                        "closed__memberCancelled",
                    ),
                },
            ],
        },
    ];

    const membershipFilterOptions: ListFilterItem<FilterByMembership>[] =
        membershipTiers.map((tier: MembershipTier) => ({
            data: {
                id: tier.id,
                name: tier.name,
            },
            selected: filterSettings.selectedMembershipTiers.includes(tier.id),
        }));

    const assignees: Agent[] = [unassignedAgent as Agent, ...agents];

    // The API client is not meant to be used directly, that's why its types are also
    // not exported from the package. This kind of code creates an extremely tight
    // coupling between the front-end and back-end and makes changes to either side
    // unnecessarily difficult.
    // TODO: This code needs to be refactored to use the `useGet` hook and to decouple
    // the types of the front-end from the back-end.
    const { apiClient } = useApiClient();
    type ResponseRequestItem = Awaited<
        ReturnType<Awaited<ReturnType<(typeof apiClient)["get"]["requests"]>>["json"]>
    >["data"]["requests"][number];

    const queryRequests = useCallback(
        async (pagination: Pagination, requestListFilters: RequestListFilters) => {
            try {
                const response = await apiClient.get.requests({
                    query: {
                        page: pagination.from.toString(),
                        size: pagination.size.toString(),
                        searchTerm: requestListFilters.searchTerm,
                        status: requestListFilters.status ?? [],
                        offeringCategory: requestListFilters.offeringCategory ?? [],
                        nextDeadlineStep: requestListFilters.nextDeadlineStep ?? [],
                        outcome: requestListFilters.outcome ?? [],
                        bookingOutcome: requestListFilters.bookingOutcome ?? [],
                        assignedTo: requestListFilters.assignedTo ?? [],
                        teamIds: requestListFilters.teamIds ?? [],
                        memberMembershipTier:
                            requestListFilters.memberMembershipTier ?? [],
                        sortBy: requestListFilters.sort?.by,
                        sortOrder: requestListFilters.sort?.order,
                    },
                });

                const json = await response.json();

                return json.data;
            } catch (error) {
                Sentry.captureException(error, {
                    extra: { message: "Failed to load requests" },
                });
            }

            return undefined;
        },
        [apiClient],
    );

    const PaginatedResults = useCallback(
        (paginatedProps: { requestListFilters: RequestListFilters }) => {
            const { requestListFilters } = paginatedProps;
            return (
                <PaginatedList<number, ResponseRequestItem>
                    className="requests-tab__results"
                    pageLength={100}
                    queryKey={["requests", requestListFilters]}
                    queryForItems={async ({ size, page }) => {
                        const data = await queryRequests(
                            {
                                from: page,
                                size,
                            },
                            requestListFilters,
                        );
                        const totalCount = data?.pagination.total ?? 0;
                        setTotal(totalCount);

                        return {
                            data: data?.requests ?? [],
                            total: totalCount,
                        };
                    }}
                    renderItem={(hit) => {
                        const {
                            id,
                            assignedTo,
                            createdAt,
                            title,
                            status,
                            conversation,
                            member,
                            ticketNumber,
                            offeringCategory,
                            assignedToName,
                            nextDeadlineStep,
                            nextDeadlineAt,
                            startDate,
                            startTime,
                            endDate,
                            endTime,
                            outcome,
                            bookingOutcome,
                            numberOfGuests,
                            memberRating,
                            memberFeedbackTimestamp,
                            locationGoogleFormattedAddress,
                        } = hit;

                        // TODO: Refactor RequestCard to use RequestListItem instead of Request
                        // once the old RequestView is fully deprecated
                        const requestForCard: Request = {
                            createdAt: firebaseTimestampFromDate(new Date(createdAt)),
                            id,
                            title,
                            status,
                            conversation,
                            member,
                            ticketNumber,
                            offeringCategory:
                                offeringCategory as Request["offeringCategory"],
                            assignedTo: assignedTo,
                            assignedToName,
                            nextDeadlineAt: nextDeadlineAt
                                ? firebaseTimestampFromDate(new Date(nextDeadlineAt))
                                : null,
                            nextDeadlineStep,
                            startDate,
                            startTime,
                            endDate,
                            endTime,
                            outcome: outcome as Request["outcome"],
                            bookingOutcome: bookingOutcome as Request["bookingOutcome"],
                            numberOfGuests,
                            memberRating,
                            memberFeedbackTimestamp: memberFeedbackTimestamp
                                ? firebaseTimestampFromDate(
                                      new Date(memberFeedbackTimestamp),
                                  )
                                : null,
                            locationGoogleFormattedAddress,
                        };

                        return (
                            <RequestCard
                                key={id}
                                request={requestForCard}
                                currentAgent={currentAgent}
                                layout={isMobile() ? "vertical" : "horizontal"}
                                showEditButton={isMobile()}
                                showStatusTag={isMobile()}
                            />
                        );
                    }}
                    renderError={() => <div>Error happened</div>}
                />
            );
        },

        [queryRequests, currentAgent],
    );

    return (
        <List.Tab>
            <List.TabHeader className="request-tab__header">
                <div className="request-tab__header-title">
                    <div className="request-tab__title">
                        <Lib.Flex.Row gap="value8">
                            <h3>Requests</h3>
                            <Tag.Medium.N90>{formatTotal(total)}</Tag.Medium.N90>
                        </Lib.Flex.Row>
                    </div>

                    <div className="request-tab__header-actions">
                        <InputSearch
                            placeholder="Search by member, request title, location or ticket number"
                            throttle={200}
                            className="request-tab__search"
                            onChange={setSearchTerm}
                        />
                    </div>
                </div>
            </List.TabHeader>
            <List.Header>
                <List.HeaderColumn className="flex-[0.45]">
                    <Lib.Flex.Row gap="value8">
                        <ListFilter<FilterByCategory>
                            items={categoryFilterOptions}
                            onChange={(newSelections) => {
                                const selectedIds = newSelections.map((it) => it.id);
                                updateUrlState({
                                    requestCategory: selectedIds,
                                });
                            }}
                            position="bottom-left"
                        />
                        <div>
                            <SortItemsBy
                                entries={[
                                    ["deadline", "Sort by deadline"],
                                    ["eventDate", "Sort by event date"],
                                    ["ticketNumber", "Sort by ticket number"],
                                ]}
                                initialSortBy={sortSettings.by || defaultSortBy}
                                initialSortOrder={
                                    urlState.sortOrder ?? defaultSortOrder
                                }
                                onChange={(by, order) => {
                                    updateUrlState({
                                        sortOrder: order,
                                        sortBy: by,
                                    });
                                }}
                            />
                        </div>
                    </Lib.Flex.Row>
                </List.HeaderColumn>
                <List.HeaderColumn className="flex-[0.25]">
                    <ListFilter<FilterByRequestStatus>
                        title="Status"
                        items={requestFilterOptions}
                        onChange={(newStatusList) => {
                            const newStatusIds = newStatusList.map((it) => it.id);
                            updateUrlState({
                                statusList: newStatusIds,
                            });
                        }}
                        position="bottom-right"
                    />
                </List.HeaderColumn>

                <List.HeaderColumn className="flex-[0.15]">
                    <ListFilter<FilterByMembership>
                        title="Membership"
                        items={membershipFilterOptions}
                        onChange={(newMembershipList) => {
                            const newMembershipIds = newMembershipList.map(
                                (it) => it.id,
                            );
                            updateUrlState({
                                membershipTier: newMembershipIds,
                            });
                        }}
                        position="bottom-right"
                    />
                </List.HeaderColumn>

                <List.HeaderColumn className="flex-[0.15]">
                    <ListFilterGroup<FilterByAssignee>
                        title="Assigned to"
                        showAllFiltersWithoutCount
                        filters={[
                            ...(teams.length > 0
                                ? [
                                      {
                                          title: "Teams",
                                          items: teamFilterItems(
                                              teams,
                                              filterSettings.selectedTeamIds,
                                          ),
                                          onChange: (selected: FilterByAssignee[]) => {
                                              updateUrlState({
                                                  teamIds: selected.map(
                                                      (item) => item.id,
                                                  ),
                                              });
                                          },
                                      },
                                  ]
                                : []),
                            {
                                title: "Active agents",
                                items: assignees
                                    .filter((agent) => !agent.disabled)
                                    .map((agent) => ({
                                        data: agent,
                                        selected:
                                            filterSettings.selectedAgents.includes(
                                                agent.id,
                                            ),
                                        count: 0,
                                    })),
                                onChange: (newAgentList) => {
                                    const newAgentIds = newAgentList.map((it) => it.id);
                                    updateUrlState({
                                        assignedTo: [
                                            ...getInactiveAssigneeSelections(
                                                assignees,
                                                filterSettings.selectedAgents,
                                            ),
                                            ...newAgentIds,
                                        ].filter(onlyUnique),
                                    });
                                },
                            },
                            {
                                title: "Inactive agents",
                                items: assignees
                                    .filter((agent) => agent.disabled)
                                    .map((agent) => ({
                                        data: agent,
                                        selected:
                                            filterSettings.selectedAgents.includes(
                                                agent.id,
                                            ),
                                        count: 0,
                                    })),
                                onChange: (newAgentList) => {
                                    const newAgentIds = newAgentList.map((it) => it.id);
                                    updateUrlState({
                                        assignedTo: [
                                            ...getActiveAssigneeSelections(
                                                assignees,
                                                filterSettings.selectedAgents,
                                            ),
                                            ...newAgentIds,
                                        ].filter(onlyUnique),
                                    });
                                },
                            },
                        ]}
                        position="bottom-right"
                    />
                </List.HeaderColumn>
            </List.Header>

            <PaginatedResults
                requestListFilters={filtersToSearchQuery(
                    searchTerm,
                    sortSettings,
                    filterSettings,
                )}
            />
        </List.Tab>
    );
};

export default RequestListView;
